import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { api, ApiError, type Consultation, type ReferralSummary, type WithdrawalRequest, egp } from '../api';
import {
  Card, StatusBadge, Button, Alert,
  PageLoader, Textarea, Table,
} from '../components/ui';
import { EarningsHistoryModal } from '../components/WithdrawalRequests';
import { useAuth } from '../auth';

type ConsultFilter = 'pending' | 'answered' | 'total';

type DoctorEarnings = {
  answeredCount: number;
  pendingCount: number;
  expiredCount: number;
  consultationRateEgp: number;
  totalEarnedEgp: number;
  availableEgp: number;
  pendingEgp: number;
  paidOutEgp: number;
  referralRegistered: number;
  referralSubscribed: number;
  referralEarnedEgp: number;
  referralAvailableEgp: number;
  answeredThisMonth: number;
  earnedThisMonthEgp: number;
  note: string;
};

function doctorDisplayName(fullName?: string | null) {
  const raw = (fullName ?? '').trim();
  if (!raw) return 'Dashboard';
  return raw.replace(/^Dr\.?\s+/i, '');
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        {children}
      </div>
    </div>
  );
}

export function DoctorDashboardPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ConsultFilter>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [refWithdrawing, setRefWithdrawing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const earningsQ = useQuery({
    queryKey: ['doctor-earnings'],
    queryFn: () => api.get<DoctorEarnings>('/api/doctor/earnings'),
  });

  const cQ = useQuery({
    queryKey: ['doctor-consultations'],
    queryFn: () => api.get<{ consultations: Consultation[] }>('/api/consultations?limit=100'),
  });

  const rQ = useQuery({
    queryKey: ['doctor-referrals'],
    queryFn: () => api.get<{ referral: ReferralSummary }>('/api/doctor/referrals'),
  });

  const wQ = useQuery({
    queryKey: ['doctor-withdrawals'],
    queryFn: () => api.get<{ requests: WithdrawalRequest[]; total: number }>('/api/doctor/withdrawals?limit=100'),
    enabled: historyOpen,
  });

  const e = earningsQ.data;
  const consultations = cQ.data?.consultations ?? [];
  const referral = rQ.data?.referral;
  const name = doctorDisplayName(me?.fullName);

  const counts = useMemo(() => {
    const answered = consultations.filter(c => c.status === 'answered').length;
    const pending = consultations.filter(c => c.status === 'pending').length;
    return {
      answered: e?.answeredCount ?? answered,
      pending: e?.pendingCount ?? pending,
      total: (e?.answeredCount ?? answered) + (e?.pendingCount ?? pending) + (e?.expiredCount ?? 0),
    };
  }, [consultations, e]);

  const filtered = useMemo(() => {
    if (filter === 'pending') return consultations.filter(c => c.status === 'pending');
    if (filter === 'answered') return consultations.filter(c => c.status === 'answered');
    return consultations;
  }, [consultations, filter]);

  const toggleRow = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setAnswerText('');
      return;
    }
    setExpandedId(id);
    setAnswerText('');
    setError('');
  };

  const submitAnswer = async (id: string) => {
    setError('');
    try {
      await api.post(`/api/consultations/${id}/answer`, { answer: answerText });
      setExpandedId(null);
      setAnswerText('');
      qc.invalidateQueries({ queryKey: ['doctor-consultations'] });
      qc.invalidateQueries({ queryKey: ['doctor-earnings'] });
      setMsg('Answer submitted.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to submit answer.');
    }
  };

  const requestPayout = async () => {
    setMsg('');
    setWithdrawing(true);
    try {
      const res = await api.post<{ amountEgp: number }>('/api/doctor/withdrawals');
      setMsg(`Payout requested for ${egp(res.amountEgp)}.`);
      qc.invalidateQueries({ queryKey: ['doctor-earnings'] });
      qc.invalidateQueries({ queryKey: ['doctor-withdrawals'] });
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Payout failed.');
    } finally {
      setWithdrawing(false);
    }
  };

  const requestReferralPayout = async () => {
    setMsg('');
    setRefWithdrawing(true);
    try {
      const res = await api.post<{ amountEgp: number }>('/api/doctor/referrals/withdraw');
      setMsg(`Referral payout requested for ${egp(res.amountEgp)}.`);
      qc.invalidateQueries({ queryKey: ['doctor-referrals'] });
      qc.invalidateQueries({ queryKey: ['doctor-earnings'] });
      qc.invalidateQueries({ queryKey: ['doctor-withdrawals'] });
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Referral payout failed.');
    } finally {
      setRefWithdrawing(false);
    }
  };

  const copyCode = async () => {
    if (!referral?.code) return;
    try {
      await navigator.clipboard.writeText(referral.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const loading = earningsQ.isLoading || cQ.isLoading || rQ.isLoading;

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink">Dr. {name}</h1>
        <p className="text-sm text-ink-soft mt-1">Consults and referrals</p>
      </div>

      {msg && (
        <Alert
          type={msg.toLowerCase().includes('fail') || msg.toLowerCase().includes('minimum') ? 'error' : 'success'}
          className="mb-4"
        >
          {msg}
        </Alert>
      )}
      {error && <Alert type="error" className="mb-4">{error}</Alert>}

      <EarningsHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        summary={[
          { label: 'Profit', amountEgp: (e?.totalEarnedEgp ?? 0) + (referral?.totalEarnedEgp ?? 0) },
          { label: 'Available', amountEgp: (e?.availableEgp ?? 0) + (referral?.availableEgp ?? 0) },
          { label: 'Pending', amountEgp: referral?.pendingRequestEgp ?? 0 },
          { label: 'Sent', amountEgp: (e?.paidOutEgp ?? 0) + (referral?.paidEgp ?? 0) },
        ]}
        requests={wQ.data?.requests ?? []}
        loading={wQ.isLoading}
        showSource
        actions={[
          ...((e?.availableEgp ?? 0) > 0 ? [{
            label: withdrawing ? 'Requesting…' : `Withdraw consults ${egp(e!.availableEgp)}`,
            onClick: requestPayout,
            loading: withdrawing,
            disabled: withdrawing,
          }] : []),
          ...((referral?.availableEgp ?? 0) > 0 ? [{
            label: refWithdrawing ? 'Requesting…' : `Withdraw referrals ${egp(referral!.availableEgp)}`,
            onClick: requestReferralPayout,
            loading: refWithdrawing,
            disabled: refWithdrawing,
          }] : []),
        ]}
      />

      {loading ? <PageLoader variant="table" /> : (
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-4 sm:p-6">
                <div className="text-xs text-ink-soft uppercase tracking-wide mb-1">Total consultations</div>
                <div className="font-heading text-3xl sm:text-4xl font-bold text-ink">{counts.total}</div>
              </Card>
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="text-left touch-manipulation"
              >
                <Card className="p-4 sm:p-6 h-full hover:border-sand/40 transition-colors">
                  <div className="text-xs text-ink-soft uppercase tracking-wide mb-1">Available</div>
                  <div className="font-heading text-3xl sm:text-4xl font-bold text-sand">
                    {egp((e?.availableEgp ?? 0) + (referral?.availableEgp ?? 0))}
                  </div>
                  <div className="text-xs text-sand-strong mt-2 font-medium">Tap for history</div>
                </Card>
              </button>
            </div>
          </div>

          <Card className="overflow-hidden">
            <div className="px-4 sm:px-5 py-4 border-b border-line space-y-3">
              <h2 className="font-heading text-lg font-bold text-ink">Consultations</h2>
              <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-1 px-1 pb-0.5">
                {([
                  { key: 'answered' as const, label: 'Answered', count: counts.answered },
                  { key: 'pending' as const, label: 'Pending', count: counts.pending },
                  { key: 'total' as const, label: 'Total', count: counts.total },
                ]).map(chip => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setFilter(chip.key)}
                    className={`shrink-0 px-3 py-2 rounded-radius-sm text-sm font-medium transition-colors touch-manipulation min-h-[40px] ${
                      filter === chip.key
                        ? 'bg-teal text-white'
                        : 'bg-bg text-ink-soft hover:text-ink border border-line'
                    }`}
                  >
                    {chip.label} {chip.count}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y divide-line">
              {filtered.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-ink-soft">
                  {filter === 'pending' ? 'No pending consults.' : filter === 'answered' ? 'No answered consults yet.' : 'No consults yet.'}
                </div>
              ) : (
                filtered.map(c => {
                  const open = expandedId === c.id;
                  return (
                    <div key={c.id} className="bg-surface">
                      <button
                        type="button"
                        onClick={() => toggleRow(c.id)}
                        className="w-full text-left px-4 sm:px-5 py-4 flex items-start gap-3 hover:bg-bg/80 transition-colors touch-manipulation min-h-[56px]"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <StatusBadge status={c.status} />
                            <span className="text-xs text-ink-faint">
                              {new Date(c.createdAt).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                          <p className={`text-sm text-ink leading-snug ${open ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}>
                            {c.question}
                          </p>
                        </div>
                        {open
                          ? <ChevronUp className="w-5 h-5 text-ink-faint shrink-0 mt-0.5" />
                          : <ChevronDown className="w-5 h-5 text-ink-faint shrink-0 mt-0.5" />}
                      </button>

                      {open && (
                        <div className="px-4 sm:px-5 pb-4 space-y-3 border-t border-line bg-bg/40">
                          <div className="pt-3">
                            <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-1">Message</div>
                            <p className="text-sm text-ink whitespace-pre-wrap break-words">{c.question}</p>
                            <div className="text-xs text-ink-faint mt-2">
                              Deadline: {new Date(c.responseDeadline).toLocaleDateString('en-GB')}
                            </div>
                          </div>

                          {c.status === 'pending' && (
                            <div className="space-y-2">
                              <Textarea
                                label="Your answer"
                                value={answerText}
                                onChange={ev => setAnswerText(ev.target.value)}
                                placeholder="Write your answer…"
                                rows={4}
                              />
                              <div className="flex flex-col-reverse sm:flex-row gap-2">
                                <Button
                                  size="sm"
                                  className="w-full sm:w-auto"
                                  onClick={() => submitAnswer(c.id)}
                                  disabled={!answerText.trim()}
                                >
                                  Submit answer
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="w-full sm:w-auto"
                                  onClick={() => { setExpandedId(null); setAnswerText(''); }}
                                >
                                  Close
                                </Button>
                              </div>
                            </div>
                          )}

                          {c.answer && (
                            <div className="p-3 bg-success-soft rounded-radius-sm text-sm text-success">
                              <div className="font-medium mb-1">Your answer</div>
                              <p className="whitespace-pre-wrap break-words">{c.answer}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <h2 className="font-heading text-lg font-bold text-ink mb-4">Referrals</h2>
            {!referral ? (
              <p className="text-sm text-ink-soft">Referral details unavailable.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-ink-soft">
                  Share this code. You earn when someone signs up with it and their account is approved, and again when they buy their first plan.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-ink-soft uppercase tracking-wide mb-1">Your referral code</div>
                    <div className="font-mono text-xl font-bold text-sand break-all">{referral.code}</div>
                  </div>
                  <Button size="sm" variant="ghost" className="w-full sm:w-auto shrink-0" onClick={copyCode}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-radius-sm border border-line bg-bg p-4">
                    <div className="text-xs text-ink-soft mb-1">Accounts approved</div>
                    <div className="text-2xl font-bold text-ink">{referral.registeredCount}</div>
                  </div>
                  <div className="rounded-radius-sm border border-line bg-bg p-4">
                    <div className="text-xs text-ink-soft mb-1">Plans bought</div>
                    <div className="text-2xl font-bold text-ink">{referral.subscribedCount}</div>
                  </div>
                  <div className="rounded-radius-sm border border-line bg-bg p-4">
                    <div className="text-xs text-ink-soft mb-1">Total earned</div>
                    <div className="text-2xl font-bold text-ink">{egp(referral.totalEarnedEgp)}</div>
                  </div>
                  <div className="rounded-radius-sm border border-line bg-bg p-4">
                    <div className="text-xs text-ink-soft mb-1">Available</div>
                    <div className="text-2xl font-bold text-ink">{egp(referral.availableEgp)}</div>
                  </div>
                </div>
                {referral.availableEgp > 0 && (
                  <Button className="w-full sm:w-auto" onClick={requestReferralPayout} disabled={refWithdrawing}>
                    {refWithdrawing ? 'Requesting…' : `Request referral payout (${egp(referral.availableEgp)})`}
                  </Button>
                )}

                <div className="pt-1 space-y-2">
                  <div className="font-medium text-ink text-sm">People who used your code</div>
                  <Table
                    data={referral.activity}
                    empty="No one has used your code yet."
                    getRowKey={row => row.id}
                    columns={[
                      {
                        key: 'name',
                        header: 'Name',
                        render: row => (
                          <span className="font-medium text-ink">{row.displayName}</span>
                        ),
                      },
                      {
                        key: 'action',
                        header: 'Action',
                        render: row => row.action,
                      },
                      {
                        key: 'earned',
                        header: 'You earned',
                        render: row => (
                          <span className="font-semibold text-ink">{egp(row.rewardEgp)}</span>
                        ),
                      },
                      {
                        key: 'status',
                        header: 'Status',
                        render: row => <StatusBadge status={row.status} />,
                      },
                      {
                        key: 'date',
                        header: 'Date',
                        render: row =>
                          new Date(row.createdAt).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          }),
                      },
                    ]}
                  />
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </Layout>
  );
}
